import React, { useEffect, useContext, useMemo, useState, useCallback } from 'react';
import styled from 'styled-components';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { useAppSelector, useAppDispatch } from '@/store';
import { 
  fetchPlayerNotifications, 
  selectPlayerNotifications, 
  selectNotificationsList, 
  selectUnreadCount,
  selectCategoriesWithCounters,
  selectNotificationsByCategory,
  selectPlayerNotificationsLoading,
  selectPlayerNotificationsError,
  selectPlayerNotificationsLastFetched,
  updateNotificationReadStatus,
  deleteNotificationFromStore,
  addNotificationToStore,
  markAllNotificationsAsRead
} from '../../../store/slices/playerNotifications';
import { getPrimaryActions as getNotificationPrimaryActions, getSecondaryActions as getNotificationSecondaryActions, getVisibleActions as getNotificationVisibleActions, executeAction as executeNotificationAction } from '../notification-manager/utils/notificationActionHelper';

type NotificationsProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: {
    limit?: number;
  };
};

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const Notifications = (componentProps: NotificationsProps) => {
  let props = componentProps;

  const dataElementContext = useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const accessToken = useAppSelector(state => state.authentication.access_token);
  const loading = useAppSelector(selectPlayerNotificationsLoading);
  const error = useAppSelector(selectPlayerNotificationsError);
  const lastFetched = useAppSelector(selectPlayerNotificationsLastFetched);
  
  const playerNotifications = useAppSelector(selectPlayerNotifications);
  const notificationsList = useAppSelector(selectNotificationsList);
  const unreadCount = useAppSelector(selectUnreadCount);
  const storeCategoriesWithCounters = useAppSelector(selectCategoriesWithCounters);
  const storeNotificationsByCategory = useAppSelector(selectNotificationsByCategory);

  const [selectedNotification, setSelectedNotification] = useState<any>(null);
  const [selectedNotificationCategory, setSelectedNotificationCategory] = useState<string>('All');

  const limit = props.properties?.limit || 20;

  // Auto-fetch notifications on mount
  useEffect(() => {
    if (accessToken && !loading && !lastFetched) {
      dispatch(fetchPlayerNotifications());
    }
  }, [accessToken, loading, lastFetched, dispatch]);

  const categoriesWithCounters = useMemo(() => {
    // Use store data if available, otherwise build it from notifications
    if (storeCategoriesWithCounters && storeCategoriesWithCounters.length > 0) {
      // Filter out empty categories
      const nonEmptyCategories = storeCategoriesWithCounters.filter(cat => cat.count > 0);
      
      // If no categories have notifications, return empty array (no categories to show)
      if (nonEmptyCategories.length === 0) {
        return [];
      }
      
      // Check if "All" category exists in non-empty categories
      const hasAllCategory = nonEmptyCategories.some(cat => cat.name === 'All');
      
      if (hasAllCategory) {
        return nonEmptyCategories;
      } else {
        // Add "All" category to the API data
        const totalCount = nonEmptyCategories.reduce((sum, cat) => sum + (cat.count || 0), 0);
        const totalUnreadCount = nonEmptyCategories.reduce((sum, cat) => sum + (cat.unreadCount || 0), 0);
        
        const result = [
          {
            name: 'All',
            count: totalCount,
            unreadCount: totalUnreadCount
          },
          ...nonEmptyCategories
        ];
        return result;
      }
    }
    
    // Fallback: build from notifications if API data not available
    if (!playerNotifications?.notifications || playerNotifications.notifications.length === 0) {
      return []; // No notifications = no categories to show
    }
    
    const categories = new Map();
    let totalCount = 0;
    let totalUnreadCount = 0;
    
    playerNotifications.notifications.forEach((notification: any) => {
      const category = notification.category || 'Other';
      const current = categories.get(category) || { count: 0, unreadCount: 0 };
      current.count++;
      totalCount++;
      if (!notification.read) {
        current.unreadCount++;
        totalUnreadCount++;
      }
      categories.set(category, current);
    });
    
    // Create the categories array with the "All" category first
    const result = [
      {
        name: 'All',
        count: totalCount,
        unreadCount: totalUnreadCount
      },
      ...Array.from(categories.entries()).map(([name, data]) => ({
        name,
        count: data.count,
        unreadCount: data.unreadCount
      }))
    ];
    
    return result;
  }, [storeCategoriesWithCounters, playerNotifications]);

  // Get notificationsByCategory from store - use API data if available, otherwise build from notifications
  const notificationsByCategory = useMemo(() => {
    // Use store data if available
    if (storeNotificationsByCategory && Object.keys(storeNotificationsByCategory).length > 0) {
      return storeNotificationsByCategory;
    }
    
    // Fallback: build from notifications if API data not available
    if (!playerNotifications?.notifications) return {};
    
    const grouped: { [key: string]: any[] } = {};
    playerNotifications.notifications.forEach((notification: any) => {
      const category = notification.category || 'Other';
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(notification);
    });
    
    return grouped;
  }, [storeNotificationsByCategory, playerNotifications]);

  const filteredNotifications = useMemo(() => {

    if (!notificationsByCategory || Object.keys(notificationsByCategory).length === 0) {
      return [];
    }

    const filtered: Array<{ id: string; name: string; items: any[] }> = [];

    if (selectedNotificationCategory && selectedNotificationCategory !== 'All') {
      
      const categoryNotifications = notificationsByCategory[selectedNotificationCategory] || [];

      const limitedNotifications = limit > 0 
        ? categoryNotifications.slice(0, limit) 
        : categoryNotifications;
      
      filtered.push({
        id: selectedNotificationCategory,
        name: selectedNotificationCategory,
        items: limitedNotifications
      });

    } else {
      const categories = ['All', ...Object.keys(notificationsByCategory).sort()];
      const sortedCategories = categories
        .filter(category => category !== 'All' && category !== 'Other')
        .sort()
        .concat(categories.includes('Other') ? ['Other'] : []);
      
      // Add categories in sorted order
      sortedCategories.forEach((category) => {
        const key = String(category);
        const categoryNotifications = notificationsByCategory[key];
        if (categoryNotifications) {
          const limitedNotifications = limit > 0
            ? categoryNotifications.slice(0, limit)
            : categoryNotifications;
          
          filtered.push({
            id: key,
            name: key,
            items: limitedNotifications
          });
        }
      });
    }

    return filtered;
  }, [notificationsByCategory, selectedNotificationCategory, limit]);

  const flattenedNotifications = useMemo(() => {
    return filteredNotifications.flatMap(category => category.items);
  }, [filteredNotifications]);

  const toggleNotificationReadStatus = useCallback(async (notificationId: string | number, markAsRead: boolean) => {
    if (!accessToken) return false;

    dispatch(updateNotificationReadStatus({ notificationId, read: markAsRead }));

    const endpoint = markAsRead ? 'read' : 'unread';

    try {
      const response = await fetch(`${window.config.playerApiUrl}/player/data/notifications/${endpoint}/${notificationId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return true;
      } else {
        dispatch(updateNotificationReadStatus({ notificationId, read: !markAsRead }));
        console.error(`Failed to mark notification as ${markAsRead ? 'read' : 'unread'}: Server error`);
      }
    } catch (error) {
      dispatch(updateNotificationReadStatus({ notificationId, read: !markAsRead }));
      console.error(`Failed to mark notification as ${markAsRead ? 'read' : 'unread'}:`, error);
    }
    return false;
  }, [accessToken, dispatch]);

  const markAsRead = useCallback(async (notificationId: string | number) => {
    return toggleNotificationReadStatus(notificationId, true);
  }, [toggleNotificationReadStatus]);

  const markAsUnread = useCallback(async (notificationId: string | number) => {
    return toggleNotificationReadStatus(notificationId, false);
  }, [toggleNotificationReadStatus]);

  const markAllAsRead = useCallback(async () => {
    if (!accessToken) return false;

    dispatch(markAllNotificationsAsRead());

    try {
      const response = await fetch(`${window.config.playerApiUrl}/player/notifications/mark-all-read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return true;
      } else {
        dispatch(fetchPlayerNotifications());
        console.error('Failed to mark all notifications as read: Server error');
      }
    } catch (error) {
      dispatch(fetchPlayerNotifications());
      console.error('Failed to mark all notifications as read:', error);
    }
    return false;
  }, [accessToken, dispatch]);

  const deleteNotification = useCallback(async (notificationId: string | number) => {
    if (!accessToken) return false;

    dispatch(deleteNotificationFromStore({ notificationId }));

    try {
      const response = await fetch(`${window.config.playerApiUrl}/player/data/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        return true;
      } else {
        dispatch(fetchPlayerNotifications());
        console.error('Failed to delete notification: Server error');
      }
    } catch (error) {
      dispatch(fetchPlayerNotifications());
      console.error('Failed to delete notification:', error);
    }
    return false;
  }, [accessToken, dispatch]);
  
  const onNotificationClick = useCallback((notification: any) => {
    setSelectedNotification(notification);
    
    if (!notification.read) {
      markAsRead(notification.id);
    }
  }, [markAsRead]);

  const onNotificationCategoryClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    const categoryId = e.currentTarget?.dataset?.categoryid;
    setSelectedNotificationCategory(categoryId ?? 'All'); 
  }, []);

  const onClickMarkAsRead = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const notificationId = e.currentTarget?.dataset?.notificationid;
    if (notificationId) {
      toggleNotificationReadStatus(notificationId, true);
    }
  }, [toggleNotificationReadStatus]);

  const onClickMarkAsUnread = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const notificationId = e.currentTarget?.dataset?.notificationid;
    if (notificationId) {
      toggleNotificationReadStatus(notificationId, false);
    }
  }, [toggleNotificationReadStatus]);

  const onClickMarkAllAsRead = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    markAllAsRead();
  }, [markAllAsRead]);

  const onClickDeleteNotification = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    const notificationId = e.currentTarget?.dataset?.notificationid;
    if (notificationId) {
      deleteNotification(notificationId);
    }
  }, [deleteNotification]);

  const refreshNotifications = useCallback(() => {
    if (accessToken) {
      dispatch(fetchPlayerNotifications());
    }
  }, [accessToken, dispatch]);

  // Single primary action (first if exists)
  const getNotificationPrimaryAction = useCallback((notification: any) => {
    const list = getNotificationPrimaryActions(notification?.actions || []);
    return list.length ? list[0] : null;
  }, []);

  // Single secondary action (first if exists)
  const getNotificationSecondaryAction = useCallback((notification: any) => {
    const list = getNotificationSecondaryActions(notification?.actions || []);
    return list.length ? list[0] : null;
  }, []);

  const getNotificationVisibleActions = useCallback((notification: any) => {
    return getNotificationVisibleActions(notification?.actions || []);
  }, []);

  // Execute action wrapper (same signature used in context)
  const executeNotificationActionWrapper = useCallback(async (notification: any, action: any) => {
    if (!action) return;
    await executeNotificationAction(action, undefined, { dispatch, currentNotification: notification });
  }, [dispatch]);

  const enhancedContext = useMemo(() => {
    const calculatedUnreadCount = flattenedNotifications?.filter(n => !n.read).length || 0;
    const storeUnreadCount = unreadCount || 0;
    const hasNotifications = (flattenedNotifications?.length || 0) > 0;
    const hasCategories = categoriesWithCounters.length > 0;

    const isDataReady = hasNotifications ? hasCategories : true;
    
    const contextData = {
      categoriesWithCounters: categoriesWithCounters.length,
      unreadCount: storeUnreadCount,
      hasNotifications,
      flattenedNotifications: flattenedNotifications?.length,
      contextChangeId: `${Date.now()}-${Math.random()}`,
      isDataReady,
      calculatedUnreadCount,
      storeUnreadCount
    };
    
    return {
      ...dataElementContext,
      
      // Data
      notifications: filteredNotifications,
      notificationsList: flattenedNotifications,
      allNotifications: notificationsList || [],
      notificationsByCategory,
      categoriesWithCounters,
      unreadCount: storeUnreadCount,
      selectedNotification,
      selectedNotificationCategory,
      
      // State
      loading,
      error,
      hasNotifications,
      isDataReady,
      
      // Actions
      markAsRead,
      markAsUnread,
      toggleNotificationReadStatus,
      markAllAsRead,
      deleteNotification,
      onNotificationClick,
      onNotificationCategoryClick,
      onClickMarkAsRead,
      onClickMarkAsUnread,
      onClickMarkAllAsRead,
      onClickDeleteNotification,
      refreshNotifications,
      setSelectedNotification,
      setSelectedNotificationCategory,
      
      // Properties
      limit,
      
      // Actions helpers for notification items
      executeNotificationAction: executeNotificationActionWrapper,
      getNotificationPrimaryAction,
      getNotificationSecondaryAction,
      getNotificationVisibleActions,
      selectedNotificationPrimaryAction: getNotificationPrimaryAction(selectedNotification),
      selectedNotificationSecondaryAction: getNotificationSecondaryAction(selectedNotification),
      
      // Debug tracking
      contextId: contextData.contextChangeId,
      lastUpdate: new Date().toISOString()
    };
  }, [
    dataElementContext,
    filteredNotifications,
    flattenedNotifications,
    notificationsByCategory,
    categoriesWithCounters,
    notificationsList,
    unreadCount,
    selectedNotification,
    selectedNotificationCategory,
    loading,
    error,
    markAsRead,
    markAsUnread,
    toggleNotificationReadStatus,
    markAllAsRead,
    deleteNotification,
    onNotificationClick,
    onNotificationCategoryClick,
    onClickMarkAsRead,
    onClickMarkAsUnread,
    onClickMarkAllAsRead,
    onClickDeleteNotification,
    refreshNotifications,
    limit,
    getNotificationPrimaryAction,
    getNotificationSecondaryAction,
    getNotificationVisibleActions,
    executeNotificationActionWrapper,
  ]);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText}>
      <DataElementContext.Provider value={enhancedContext}>
        {props.children}
      </DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default Notifications;
